import { Bridge } from './bridge';
import { SDK_VERSION } from './version';
import { applyEnvironment, readBoot, resolveInsets } from './environment';
import type { MiniAppEnvironment } from './environment';
import type {
    BridgeMessage,
    ExodeMiniAppConfig,
    MiniAppCapability,
    MiniAppCommandMap,
    MiniAppCommandResult,
    MiniAppCommandType,
    MiniAppComposerParams,
    MiniAppContext,
    MiniAppEdgeToEdge,
    MiniAppEventMap,
    MiniAppEventType,
    MiniAppHapticImpactStyle,
    MiniAppHapticNotificationType,
    MiniAppHeaderParams,
    MiniAppInitPayload,
    MiniAppInitResult,
    MiniAppInsets,
    MiniAppMainButtonParams,
    MiniAppOrientation,
    MiniAppPopupButton,
    MiniAppPopupParams,
    MiniAppPromptParams,
    MiniAppShareParams,
    MiniAppViewport,
} from './types';


const DEFAULT_TIMEOUT = 10_000;

/** A drag of the content down by this much hides the keyboard of the native composer, px */
const KEYBOARD_DRAG_DISTANCE = 30;

/** Fields of the context each event of the host replaces */
const CONTEXT_EVENTS: Partial<Record<MiniAppEventType, keyof MiniAppContext>> = {
    'theme:changed': 'theme',
    'user:updated': 'user',
    'school:updated': 'school',
    'config:updated': 'config',
    'viewport:changed': 'viewport',
};

export class ExodeMiniApp {

    /** Navigation commands */
    readonly route = {
        navigate: (path: string, params?: Record<string, string>): Promise<void> => {
            return this.sendCommand('navigate', { path, params });
        },
        back: (): Promise<void> => {
            return this.sendCommand('navigate:back', undefined);
        },
    };

    /** UI control commands */
    readonly ui = {
        showSnackbar: (params: MiniAppCommandMap['showSnackbar']): Promise<void> => {
            return this.sendCommand('showSnackbar', params);
        },
        setTabbarVisible: (visible: boolean): Promise<void> => {
            return this.sendCommand('setTabbarVisible', { visible });
        },
        setHeaderVisible: (visible: boolean): Promise<void> => {
            return this.sendCommand('setHeaderVisible', { visible });
        },
        minimize: (): Promise<void> => {
            return this.sendCommand('minimize', undefined);
        },
        close: (): Promise<void> => {
            return this.sendCommand('close', undefined);
        },
        /** A link in the browser inside the app (`external` — the system one); a new tab on the web */
        openLink: async (url: string, options: { external?: boolean } = {}): Promise<void> => {
            if (!this.isSupported('openLink')) {
                window.open(url, '_blank', 'noopener,noreferrer');

                return;
            }

            return this.sendCommand('link:open', { url, external: options.external });
        },
        /** The «Share» sheet of the phone (of the browser); resolves whether it was shared */
        share: async (params: MiniAppShareParams): Promise<boolean> => {
            if (!this.isSupported('share')) {
                return webShare(params);
            }

            return !!(await this.sendCommand('share', params))?.shared;
        },
        /** Copies the text: the clipboard of the app, of the browser outside it */
        copyText: async (text: string): Promise<boolean> => {
            if (!this.isSupported('clipboard')) {
                return webCopy(text);
            }

            return !!(await this.sendCommand('clipboard:write', { text }))?.copied;
        },
    };

    /** Popups of the host: in the app — its native alert and prompt */
    readonly popup = {
        /** Resolves with the id of the pressed button, null — closed without a choice */
        show: async (params: MiniAppPopupParams): Promise<string | null> => {
            if (!this.isSupported('popup')) {
                return webPopup(params);
            }

            const result = await this.sendCommand('popup:show', params, 0);

            return result?.buttonId ?? null;
        },
        alert: async (message: string, title?: string): Promise<void> => {
            await this.popup.show({ title, message, buttons: [ { type: 'ok' } ] });
        },
        /** Resolves true for «OK» */
        confirm: async (
            message: string,
            options: { title?: string; okText?: string; cancelText?: string; destructive?: boolean } = {},
        ): Promise<boolean> => {
            const buttonId = await this.popup.show({
                title: options.title,
                message,
                buttons: [
                    { id: 'cancel', type: 'cancel', text: options.cancelText },
                    {
                        id: 'ok',
                        type: options.destructive ? 'destructive' : (options.okText ? 'default' : 'ok'),
                        text: options.okText,
                    },
                ],
            });

            return buttonId === 'ok';
        },
        /** Resolves with the text entered, null — cancelled */
        prompt: async (params: MiniAppPromptParams): Promise<string | null> => {
            if (!this.isSupported('prompt')) {
                return window.prompt([ params.title, params.message ].filter(Boolean).join('\n\n'), params.value ?? '');
            }

            const result = await this.sendCommand('popup:prompt', params, 0);

            return result?.value ?? null;
        },
    };

    /** The taptic engine of the phone (no-op where there is none) */
    readonly haptic = {
        impact: (style: MiniAppHapticImpactStyle = 'medium'): Promise<void> => {
            return this.sendHaptic({ type: 'impact', style }, 10);
        },
        notification: (type: MiniAppHapticNotificationType): Promise<void> => {
            return this.sendHaptic({ type: 'notification', style: type }, type === 'error' ? [ 20, 60, 20 ] : 20);
        },
        selection: (): Promise<void> => {
            return this.sendHaptic({ type: 'selection' }, 5);
        },
    };

    /** The header of the host: the title and buttons with the icons of the host (`header:button`) */
    readonly header = {
        set: (params: MiniAppHeaderParams): Promise<void> => {
            return this.sendIfSupported('header', 'header:set', params);
        },
    };

    /**
     * «Back» of the header goes to the app (`backButton:pressed`) instead of leaving it: for the
     * screens of the app itself
     */
    readonly backButton = {
        show: (): Promise<void> => {
            return this.sendIfSupported('backButton', 'backButton:set', { visible: true });
        },
        hide: (): Promise<void> => {
            return this.sendIfSupported('backButton', 'backButton:set', { visible: false });
        },
    };

    /** The main button pinned to the bottom of the screen: the native one in the app (`mainButton:pressed`) */
    readonly mainButton = {
        set: (params: MiniAppMainButtonParams): Promise<void> => {
            return this.sendIfSupported('mainButton', 'mainButton:set', params);
        },
        hide: (): Promise<void> => {
            return this.sendIfSupported('mainButton', 'mainButton:set', { visible: false });
        },
    };

    /**
     * The composer of the host at the bottom of the screen, as in its chats: the native one in the
     * app — the field, attachments, the mic. The text comes in `composer:submit`, the recordings
     * in `composer:voice`
     */
    readonly composer = {
        set: (params: MiniAppComposerParams): Promise<void> => {
            this.watchKeyboardDrag(params.visible);

            return this.sendIfSupported('composer', 'composer:set', params);
        },
        hide: (): Promise<void> => {
            this.watchKeyboardDrag(false);

            return this.sendIfSupported('composer', 'composer:set', { visible: false });
        },
        dismissKeyboard: (): Promise<void> => {
            return this.sendIfSupported('composer', 'composer:dismissKeyboard', undefined);
        },
    };

    /** The whole screen and its orientation */
    readonly viewport = {
        /** Hides the header and the tab bar of the host: the app takes the whole screen */
        requestFullscreen: async (): Promise<boolean> => {
            if (!this.isSupported('fullscreen')) {
                return webFullscreen(true);
            }

            return !!(await this.sendCommand('fullscreen:set', { fullscreen: true }))?.isFullscreen;
        },
        exitFullscreen: async (): Promise<boolean> => {
            if (!this.isSupported('fullscreen')) {
                return webFullscreen(false);
            }

            return !!(await this.sendCommand('fullscreen:set', { fullscreen: false }))?.isFullscreen;
        },
        /** Turns the screen and holds it (in the app the rest of it turns too, until unlocked) */
        lockOrientation: async (orientation: MiniAppOrientation): Promise<void> => {
            if (!this.isSupported('orientation')) {
                return webLockOrientation(orientation);
            }

            return this.sendCommand('orientation:lock', { orientation });
        },
        unlockOrientation: async (): Promise<void> => {
            if (!this.isSupported('orientation')) {
                try {
                    (screen.orientation as ScreenOrientation & { unlock?: () => void })?.unlock?.();
                } catch {
                    // the browser holds no lock
                }

                return;
            }

            return this.sendCommand('orientation:unlock', undefined);
        },
    };

    private bridge: Bridge | null = null;
    private context: MiniAppContext | null = null;
    private readonly config: Required<Omit<ExodeMiniAppConfig, 'edgeToEdge' | 'targetOrigin'>> & {
        targetOrigin?: string;
        edgeToEdge: MiniAppEdgeToEdge;
    };
    private eventListeners = new Map<string, Set<(payload: unknown) => void>>();
    private contextListeners = new Set<(context: MiniAppContext) => void>();
    private keyboardDragOff: (() => void) | null = null;

    constructor(config: ExodeMiniAppConfig) {
        if (!config.appId) {
            throw new Error('ExodeMiniApp: appId is required');
        }

        this.config = {
            appId: config.appId,
            targetOrigin: config.targetOrigin,
            timeout: config.timeout ?? DEFAULT_TIMEOUT,
            edgeToEdge: config.edgeToEdge ?? false,
            applyEnvironment: config.applyEnvironment ?? true,
            applyTheme: config.applyTheme ?? false,
        };

        // What the host said before the handshake (the name of the frame): no flash, no jump
        this.applyEnvironment(readBoot());
    }

    async init(): Promise<MiniAppContext> {
        if (this.bridge) {
            throw new Error('ExodeMiniApp is already initialized');
        }

        if (!window.parent || window.parent === window) {
            throw new Error('ExodeMiniApp must be used inside an iframe');
        }

        // Without an explicit origin the messages go to the page the frame is embedded in, not to anyone
        const targetOrigin = this.config.targetOrigin || parentOrigin() || '*';

        if (targetOrigin === '*') {
            console.warn(
                '[ExodeMiniApp] targetOrigin is not set — messages from any parent origin will be accepted. '
                + 'Pass the origin of your school explicitly.',
            );
        }

        this.bridge = new Bridge({
            source: 'exode-miniapp',
            targetOrigin,
            targetWindow: window.parent,
        });

        this.subscribeToEvents();

        const result = await this.bridge.sendRequest<MiniAppInitPayload, MiniAppInitResult>(
            'init',
            {
                appId: this.config.appId,
                sdkVersion: SDK_VERSION,
                edgeToEdge: this.config.edgeToEdge !== false,
            },
            this.config.timeout,
        );

        this.context = result.context;

        this.applyEnvironment(this.context);
        this.notifyContext();

        return this.context;
    }

    getContext(): MiniAppContext | null {
        return this.context;
    }

    /** Whether the host does it (older hosts declare nothing: the SDK falls back to the web) */
    isSupported(capability: MiniAppCapability): boolean {
        return !!this.context?.capabilities?.includes(capability);
    }

    /** Where the frame stands; null before the handshake or with a host before 0.4 */
    getViewport(): MiniAppViewport | null {
        return this.context?.viewport ?? null;
    }

    /** What the content keeps free at each edge: the chrome of the host or the safe area */
    getInsets(): MiniAppInsets {
        return resolveInsets(this.context?.viewport);
    }

    on<E extends MiniAppEventType>(event: E, handler: (payload: MiniAppEventMap[E]) => void): () => void {
        const set = this.eventListeners.get(event);
        const wrappedHandler = handler as (payload: unknown) => void;

        if (set) {
            set.add(wrappedHandler);
        } else {
            this.eventListeners.set(event, new Set([ wrappedHandler ]));
        }

        return () => {
            this.eventListeners.get(event)?.delete(wrappedHandler);
        };
    }

    /** Any change of the context (the handshake, an update of the host) */
    onContextChange(listener: (context: MiniAppContext) => void): () => void {
        this.contextListeners.add(listener);

        return () => {
            this.contextListeners.delete(listener);
        };
    }

    destroy(): void {
        this.watchKeyboardDrag(false);
        this.bridge?.destroy();
        this.bridge = null;
        this.context = null;
        this.eventListeners.clear();
        this.contextListeners.clear();
    }

    private async sendCommand<C extends MiniAppCommandType>(
        type: C,
        payload: MiniAppCommandMap[C],
        timeout = this.config.timeout,
    ): Promise<MiniAppCommandResult<C>> {
        if (!this.bridge) {
            throw new Error('ExodeMiniApp is not initialized. Call init() first.');
        }

        return this.bridge.sendRequest<MiniAppCommandMap[C], MiniAppCommandResult<C>>(
            `command:${type}`,
            payload,
            timeout,
        );
    }

    /** A command of a capability the host may not have: without it — nothing (the app keeps its own UI) */
    private async sendIfSupported<C extends MiniAppCommandType>(
        capability: MiniAppCapability,
        type: C,
        payload: MiniAppCommandMap[C],
    ): Promise<void> {
        if (!this.isSupported(capability)) {
            return;
        }

        await this.sendCommand(type, payload);
    }

    private async sendHaptic(
        params: MiniAppCommandMap['haptic'],
        vibration: number | number[],
    ): Promise<void> {
        if (!this.isSupported('haptic')) {
            try {
                // Chrome refuses (and warns) a vibration with no gesture of the user
                if (navigator.userActivation?.isActive !== false) {
                    navigator.vibrate?.(vibration);
                }
            } catch {
                // no vibration in this browser
            }

            return;
        }

        await this.sendCommand('haptic', params);
    }

    /** The scheme, the colors and the insets of the host on <html> (the boot of the frame, then the context) */
    private applyEnvironment(env: MiniAppEnvironment | null): void {
        if (!env || !this.config.applyEnvironment) {
            return;
        }

        applyEnvironment(env, {
            edgeToEdge: this.config.edgeToEdge,
            applyTheme: this.config.applyTheme,
        });
    }

    private notifyContext(): void {
        const { context } = this;

        if (!context) {
            return;
        }

        for (const listener of this.contextListeners) {
            try {
                listener(context);
            } catch {
                // one listener must not break the others
            }
        }
    }

    /**
     * The content of the frame is out of reach of the host: a drag of it down hides the keyboard of
     * the native composer, as the chats of the host do (once per touch)
     */
    private watchKeyboardDrag(enabled: boolean): void {
        if (!enabled || this.context?.platform !== 'native' || !this.isSupported('composer')) {
            this.keyboardDragOff?.();
            this.keyboardDragOff = null;

            return;
        }

        if (this.keyboardDragOff) {
            return;
        }

        let startY: number | null = null;

        const onStart = (event: TouchEvent) => {
            startY = event.touches[0]?.clientY ?? null;
        };

        const onMove = (event: TouchEvent) => {
            const y = event.touches[0]?.clientY;

            if (startY === null || y === undefined || y - startY < KEYBOARD_DRAG_DISTANCE) {
                return;
            }

            startY = null;

            this.sendCommand('composer:dismissKeyboard', undefined).catch(() => {});
        };

        document.addEventListener('touchstart', onStart, { passive: true, capture: true });
        document.addEventListener('touchmove', onMove, { passive: true, capture: true });

        this.keyboardDragOff = () => {
            document.removeEventListener('touchstart', onStart, { capture: true });
            document.removeEventListener('touchmove', onMove, { capture: true });
        };
    }

    private subscribeToEvents(): void {
        if (!this.bridge) return;

        this.bridge.on('event', (message: BridgeMessage) => {
            const eventPayload = message.payload;
            if (!eventPayload || typeof eventPayload !== 'object') return;

            const { type, payload } = eventPayload as { type?: string; payload?: unknown };
            if (typeof type !== 'string') return;

            if (this.context && payload && typeof payload === 'object') {
                const field = CONTEXT_EVENTS[type as MiniAppEventType];

                if (type === 'context:updated') {
                    Object.assign(this.context, payload);
                } else if (field) {
                    Object.assign(this.context, { [field]: payload });
                }

                if (type === 'context:updated' || field) {
                    this.applyEnvironment(this.context);
                    this.notifyContext();
                }
            }

            const handlers = this.eventListeners.get(type);
            if (handlers) {
                for (const handler of handlers) {
                    try {
                        handler(payload);
                    } catch {
                        // prevent handler error from breaking event loop
                    }
                }
            }
        });
    }

}

/** The popup of the browser: one button — alert, two and more — confirm of the first «yes» and «no» */
function webPopup(params: MiniAppPopupParams): string | null {
    const buttons: MiniAppPopupButton[] = params.buttons?.length ? params.buttons : [ { type: 'close' } ];
    const text = [ params.title, params.message ].filter(Boolean).join('\n\n');
    const idOf = (button?: MiniAppPopupButton) => button ? (button.id ?? button.type ?? 'default') : null;

    if (buttons.length === 1) {
        window.alert(text);

        return idOf(buttons[0]);
    }

    const cancel = buttons.find(({ type }) => type === 'cancel' || type === 'close');
    const accept = buttons.find((button) => button !== cancel);

    return window.confirm(text) ? idOf(accept) : idOf(cancel);
}

/** The «Share» sheet of the browser; none — nothing shared (copying is the app's own choice) */
async function webShare(params: MiniAppShareParams): Promise<boolean> {
    try {
        if (!navigator.share) {
            return false;
        }

        await navigator.share(params);

        return true;
    } catch {
        return false;
    }
}

/**
 * The origin of the page the frame is embedded in. Firefox has no ancestorOrigins: the referrer of
 * the first load, unless it is the app itself (a navigation inside the frame)
 */
function parentOrigin(): string | null {
    try {
        const ancestor = window.location.ancestorOrigins?.[0];

        if (ancestor && ancestor !== 'null') {
            return ancestor;
        }
    } catch {
        // no ancestorOrigins in this browser
    }

    try {
        const origin = document.referrer ? new URL(document.referrer).origin : null;

        return origin && origin !== window.location.origin && origin !== 'null' ? origin : null;
    } catch {
        return null;
    }
}

async function webCopy(text: string): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(text);

        return true;
    } catch {
        return false;
    }
}

async function webFullscreen(enter: boolean): Promise<boolean> {
    try {
        if (enter && !document.fullscreenElement) {
            await document.documentElement.requestFullscreen?.();
        }

        if (!enter && document.fullscreenElement) {
            await document.exitFullscreen?.();
        }
    } catch {
        // the frame is not allowed the fullscreen
    }

    return !!document.fullscreenElement;
}

async function webLockOrientation(orientation: MiniAppOrientation): Promise<void> {
    try {
        await (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> })?.lock?.(orientation);
    } catch {
        // the browser locks only a fullscreen page, or not at all
    }
}
